import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
} from "@mui/material";
import { Add, Close, Delete, Edit, Visibility, LocalOffer } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import promoService from "../../services/promoService";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import ConfirmDialog from "../../components/common/ConfirmDialog";

const blankForm = {
  name: "",
  description: "",
  code: "",
  discountAmount: "",
  startDate: "",
  endDate: "",
  maxRedemptions: "",
  minCbf: "",
  maxCbf: "",
  minWeightLb: "",
  maxWeightLb: "",
  active: true,
  oncePerCustomer: false,
};

// A code-redemption promo — the only mechanic. Customer types the code in
// while creating a shipment (see ShipmentForm.jsx / mobile
// ShipmentFormScreen.jsx's "Promo Code" section) and redeems it there for
// an UNGATED promo (no weight/CBF requirement) — that's immediate,
// atomic, and permanent. A promo with a weight/CBF cap can't be confirmed
// at that point (those aren't known until the warehouse weighs the
// package in), so it stays pending and gets resolved automatically the
// first time an admin generates an invoice for that shipment. Either way,
// `maxRedemptions` caps how many times the code can be used in total,
// across every customer combined — leave blank for unlimited.
const PromoFormDialog = ({ open, onClose, onSubmit, initial, loading }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(blankForm);

  useEffect(() => {
    setForm(
      initial
        ? {
          name: initial.name || "",
          description: initial.description || "",
          code: initial.code || "",
          discountAmount: initial.discountAmount ?? "",
          startDate: initial.startDate ? dayjs(initial.startDate).format("YYYY-MM-DD") : "",
          endDate: initial.endDate ? dayjs(initial.endDate).format("YYYY-MM-DD") : "",
          maxRedemptions: initial.maxRedemptions ?? "",
          minCbf: initial.minCbf ?? "",
          maxCbf: initial.maxCbf ?? "",
          minWeightLb: initial.minWeightLb ?? "",
          maxWeightLb: initial.maxWeightLb ?? "",
          active: initial.active !== undefined ? initial.active : true,
          oncePerCustomer: !!initial.oncePerCustomer,
        }
        : blankForm,
    );
  }, [initial, open]);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));
  const hasSizeGate = form.minCbf !== "" || form.maxCbf !== "" || form.minWeightLb !== "" || form.maxWeightLb !== "";

  const canSubmit = form.name.trim() && form.code.trim() && form.discountAmount && form.startDate && form.endDate;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography fontWeight={700}>{isEdit ? "Edit Promo" : "New Promo"}</Typography>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2.5 }}>
        <TextField label="Promo Name *" value={form.name} onChange={set("name")} placeholder="e.g. Spring 2026 Move-In Special" />
        <TextField
          label="Description (optional)"
          value={form.description}
          onChange={set("description")}
          multiline
          rows={2}
          placeholder="Shown to admins only — not to customers"
        />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField
            label="Promo Code *"
            value={form.code}
            onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
            placeholder="e.g. SAVE15"
          />
          <TextField
            label="Discount Amount (CI$) *"
            type="number"
            value={form.discountAmount}
            onChange={set("discountAmount")}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField
            label="Redeemable After *"
            type="date"
            value={form.startDate}
            onChange={set("startDate")}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Redeemable Before *"
            type="date"
            value={form.endDate}
            onChange={set("endDate")}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Box>

        <TextField
          label="Max Redemptions (optional)"
          type="number"
          value={form.maxRedemptions}
          onChange={set("maxRedemptions")}
          inputProps={{ min: 1, step: 1 }}
          helperText="Total times this code can be used, across every customer combined. Leave blank for unlimited."
        />

        <Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Shipment size requirement (optional)
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 2 }}>
            <TextField label="Min CBF" type="number" value={form.minCbf} onChange={set("minCbf")} inputProps={{ min: 0, step: 0.1 }} />
            <TextField label="Max CBF" type="number" value={form.maxCbf} onChange={set("maxCbf")} inputProps={{ min: 0, step: 0.1 }} />
            <TextField
              label="Min Weight (lbs)"
              type="number"
              value={form.minWeightLb}
              onChange={set("minWeightLb")}
              inputProps={{ min: 0, step: 1 }}
            />
            <TextField
              label="Max Weight (lbs)"
              type="number"
              value={form.maxWeightLb}
              onChange={set("maxWeightLb")}
              inputProps={{ min: 0, step: 1 }}
            />
          </Box>
          {hasSizeGate && (
            <Alert severity="info" sx={{ borderRadius: 2, mt: 1.5 }}>
              Weight/CBF aren't known until the warehouse weighs the package in — this promo won't confirm/apply until
              an admin generates the invoice for a qualifying shipment, not at the moment the customer redeems the
              code.
            </Alert>
          )}
        </Box>

        <FormControlLabel
          control={<Switch checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />}
          label="Active — customers can currently redeem this promo"
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.oncePerCustomer}
              onChange={(e) => setForm((p) => ({ ...p, oncePerCustomer: e.target.checked }))}
            />
          }
          label="Limit to one redemption per customer — even if Max Redemptions allows more uses overall"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSubmit || loading} onClick={() => onSubmit(form)}>
          {loading ? <CircularProgress size={18} color="inherit" /> : isEdit ? "Save Changes" : "Create Promo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RedemptionsDialog = ({ promoId, onClose }) => {
  const [promo, setPromo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!promoId) return;
    setLoading(true);
    promoService
      .getOne(promoId)
      .then(({ data }) => setPromo(data.data.promo))
      .finally(() => setLoading(false));
  }, [promoId]);

  return (
    <Dialog open={!!promoId} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography fontWeight={700}>{promo ? `Redemptions — ${promo.name}` : "Redemptions"}</Typography>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <SkeletonLoader rows={3} />
        ) : !promo?.redemptions?.length ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No customer has redeemed this promo yet.
            </Typography>
          </Box>
        ) : (
          promo.redemptions.map((r, i) => (
            <Box
              key={i}
              sx={{
                px: 2.5,
                py: 1.5,
                display: "flex",
                justifyContent: "space-between",
                borderBottom: i < promo.redemptions.length - 1 ? "1px solid" : "none",
                borderColor: "divider",
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {r.customer?.firstName} {r.customer?.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.customer?.email}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {r.shipment?.trackingNumber || "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {r.invoice?.invoiceNumber ? `Billed on ${r.invoice.invoiceNumber}` : "Not yet billed"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {dayjs(r.redeemedAt).format("MMM D, YYYY")}
                </Typography>
              </Box>
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AdminPromos = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [redemptionsTarget, setRedemptionsTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await promoService.getAll();
      setPromos(data.data.promos || []);
    } catch {
      enqueueSnackbar("Failed to load promos", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (form) => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        discountAmount: Number(form.discountAmount),
        maxRedemptions: form.maxRedemptions === "" ? null : Number(form.maxRedemptions),
        minCbf: form.minCbf === "" ? null : Number(form.minCbf),
        maxCbf: form.maxCbf === "" ? null : Number(form.maxCbf),
        minWeightLb: form.minWeightLb === "" ? null : Number(form.minWeightLb),
        maxWeightLb: form.maxWeightLb === "" ? null : Number(form.maxWeightLb),
      };
      if (editTarget) {
        await promoService.update(editTarget._id, payload);
        enqueueSnackbar("Promo updated", { variant: "success" });
      } else {
        await promoService.create(payload);
        enqueueSnackbar("Promo created", { variant: "success" });
      }
      setFormOpen(false);
      setEditTarget(null);
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to save promo", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await promoService.delete(deleteTarget._id);
      enqueueSnackbar("Promo deleted", { variant: "success" });
      setDeleteTarget(null);
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to delete promo", { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (promo) => {
    try {
      await promoService.update(promo._id, { active: !promo.active });
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to update promo", { variant: "error" });
    }
  };

  const sizeGateLabel = (p) => {
    const parts = [];
    if (p.minCbf != null || p.maxCbf != null) {
      parts.push(
        p.minCbf != null && p.maxCbf != null
          ? `${p.minCbf}–${p.maxCbf} CBF`
          : p.minCbf != null
            ? `≥${p.minCbf} CBF`
            : `≤${p.maxCbf} CBF`,
      );
    }
    if (p.minWeightLb != null || p.maxWeightLb != null) {
      parts.push(
        p.minWeightLb != null && p.maxWeightLb != null
          ? `${p.minWeightLb}–${p.maxWeightLb} lbs`
          : p.minWeightLb != null
            ? `≥${p.minWeightLb} lbs`
            : `≤${p.maxWeightLb} lbs`,
      );
    }
    return parts.join(", ");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Promos"
        subtitle="Customers redeem a code while creating a shipment — the discount applies on their next invoice"
        action={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditTarget(null);
              setFormOpen(true);
            }}
          >
            New Promo
          </Button>
        }
      />

      <Card>
        {loading ? (
          <SkeletonLoader rows={4} />
        ) : promos.length === 0 ? (
          <EmptyState
            icon="🏷️"
            title="No promos yet"
            description="Create a promo code for customers to redeem while creating a shipment."
            actionLabel="New Promo"
            onAction={() => setFormOpen(true)}
          />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Promo</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Window</TableCell>
                  <TableCell>Size Requirement</TableCell>
                  <TableCell align="right">Discount</TableCell>
                  <TableCell align="right">Redeemed</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {promos.map((p) => (
                  <TableRow key={p._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>
                        {p.name}
                      </Typography>
                      {p.description && (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap maxWidth={220}>
                          {p.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={p.code} variant="outlined" sx={{ fontFamily: "monospace", fontWeight: 700 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, whiteSpace: "nowrap" }}>
                      {dayjs(p.startDate).format("MMM D, YYYY")} – {dayjs(p.endDate).format("MMM D, YYYY")}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, whiteSpace: "nowrap" }}>
                      {sizeGateLabel(p) || <Typography variant="caption" color="text.secondary">None</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      CI${p.discountAmount?.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        icon={<LocalOffer sx={{ fontSize: 13 }} />}
                        label={p.maxRedemptions != null ? `${p.redemptionCount || 0} / ${p.maxRedemptions}` : p.redemptionCount || 0}
                        variant="outlined"
                        onClick={() => setRedemptionsTarget(p._id)}
                        clickable
                      />
                      {p.oncePerCustomer && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                          1 per customer
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={p.active ? "Active" : "Paused"}
                        color={p.active ? "success" : "default"}
                        onClick={() => handleToggleActive(p)}
                        clickable
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => setRedemptionsTarget(p._id)} title="View redemptions">
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditTarget(p);
                          setFormOpen(true);
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={(p.redemptionCount || 0) > 0}
                        onClick={() => setDeleteTarget(p)}
                        title={(p.redemptionCount || 0) > 0 ? "Already redeemed — pause it instead" : "Delete"}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

      <PromoFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditTarget(null);
        }}
        onSubmit={handleSubmit}
        initial={editTarget}
        loading={saving}
      />

      <RedemptionsDialog promoId={redemptionsTarget} onClose={() => setRedemptionsTarget(null)} />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Promo?"
        message={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

export default AdminPromos;
